
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import PageTransition from '@/components/utils/PageTransition';
import { Camera, ArrowUpRight, CheckCircle2, BrainCircuit, Code, BarChart2, EyeIcon, ScanLine } from 'lucide-react';

const ComputerVision = () => {
  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow pt-32 pb-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <span className="bg-purple-50 dark:bg-purple-950 text-purple-600 dark:text-purple-400 px-3 py-1 rounded-full text-sm font-medium">
                Computer Vision
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                Advanced <span className="text-primary">Computer Vision</span> Solutions
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Enable machines to understand and interpret visual information from the world.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
              <div>
                <h2 className="text-2xl font-bold mb-6">Transform How Machines See and Understand</h2>
                <p className="text-muted-foreground mb-6">
                  Our computer vision solutions enable machines to interpret, analyze, and make decisions based on visual data. 
                  From simple image recognition to complex scene understanding, our systems provide reliable, accurate, and 
                  scalable solutions for a wide range of industries.
                </p>
                <p className="text-muted-foreground mb-8">
                  Whether you need to automate quality control, enhance security systems, or develop innovative 
                  customer experiences, our AI-powered computer vision technology delivers intelligent insights from visual data.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {[
                    'Object Detection', 
                    'Image Classification', 
                    'Visual Search',
                    'Video Analytics',
                    'Facial Recognition',
                    'Scene Understanding',
                    'OCR Solutions',
                    'Pose Estimation'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Link 
                  to="/contact" 
                  className="glass-button-primary px-6 py-3 text-base font-medium inline-block"
                >
                  Start Your Project
                  <ArrowUpRight className="ml-2 h-4 w-4 inline-block" />
                </Link>
              </div>
              
              <div className="bg-purple-50 dark:bg-purple-950/30 rounded-2xl p-8">
                <div className="flex items-center mb-6">
                  <Camera className="h-8 w-8 text-purple-600 dark:text-purple-400 mr-3" />
                  <h3 className="text-xl font-bold">How Our Computer Vision Works</h3>
                </div>
                <div className="space-y-6">
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="bg-white dark:bg-gray-800 h-10 w-10 rounded-full flex items-center justify-center shadow-md">
                        <span className="text-purple-600 dark:text-purple-400 font-semibold">1</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Data Collection & Processing</h4>
                      <p className="text-sm text-muted-foreground">
                        We collect, clean, and preprocess visual data from various sources, ensuring high-quality inputs for our models.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="bg-white dark:bg-gray-800 h-10 w-10 rounded-full flex items-center justify-center shadow-md">
                        <span className="text-purple-600 dark:text-purple-400 font-semibold">2</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Model Training & Optimization</h4>
                      <p className="text-sm text-muted-foreground">
                        We train customized models on your specific visual tasks, then optimize for accuracy and performance.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="bg-white dark:bg-gray-800 h-10 w-10 rounded-full flex items-center justify-center shadow-md">
                        <span className="text-purple-600 dark:text-purple-400 font-semibold">3</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Integration & Deployment</h4>
                      <p className="text-sm text-muted-foreground">
                        We integrate the computer vision system into your existing infrastructure and deploy it to your environment.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="bg-white dark:bg-gray-800 h-10 w-10 rounded-full flex items-center justify-center shadow-md">
                        <span className="text-purple-600 dark:text-purple-400 font-semibold">4</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Monitoring & Improvement</h4>
                      <p className="text-sm text-muted-foreground">
                        We continuously monitor performance and refine the system to adapt to new visual challenges.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-20">
              <h2 className="text-2xl font-bold mb-8 text-center">Key Applications</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    title: 'Manufacturing Quality Control',
                    icon: <EyeIcon className="h-6 w-6" />,
                    description: 'Automated visual inspection systems to identify defects and ensure product quality.'
                  },
                  {
                    title: 'Retail Analytics',
                    icon: <BarChart2 className="h-6 w-6" />,
                    description: 'Customer behavior analysis, inventory management, and automated checkout systems.'
                  },
                  {
                    title: 'Security & Surveillance',
                    icon: <ScanLine className="h-6 w-6" />,
                    description: 'Advanced monitoring systems with anomaly detection and object tracking capabilities.'
                  },
                  {
                    title: 'Healthcare Diagnostics',
                    icon: <BrainCircuit className="h-6 w-6" />,
                    description: 'Medical image analysis tools for faster and more accurate diagnoses.'
                  },
                  {
                    title: 'Autonomous Navigation',
                    icon: <Camera className="h-6 w-6" />,
                    description: 'Visual perception systems for robots, drones, and autonomous vehicles.'
                  },
                  {
                    title: 'Smart Agriculture',
                    icon: <Code className="h-6 w-6" />,
                    description: 'Crop monitoring, disease detection, and automated harvesting solutions.'
                  }
                ].map((app, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-subtle p-6 border border-gray-100 dark:border-gray-700">
                    <div className="bg-purple-50 dark:bg-purple-950/50 p-3 rounded-full w-fit mb-4">
                      {app.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{app.title}</h3>
                    <p className="text-muted-foreground">{app.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 rounded-2xl p-8 mb-20">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2">Ready to Transform Your Visual Data?</h2>
                <p className="text-muted-foreground">
                  Contact our team to discuss how our computer vision solutions can help your business.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link 
                  to="/contact" 
                  className="glass-button-primary px-6 py-3 text-center font-medium"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </PageTransition>
  );
};

export default ComputerVision;
